/** Shortest run of consecutive look-alike entries folded into one row. */
export const REPEAT_GROUP_MIN = 3;

export type EventLogRow<T> = { kind: "single"; item: T } | { kind: "group"; key: string; items: T[] };

/**
 * Folds runs of at least `minRun` consecutive entries sharing a `groupKey` into one group,
 * keeping the display order. An entry without a key is never grouped.
 */
export function groupRepeatedEvents<T extends { groupKey?: string }>(items: T[], minRun: number = REPEAT_GROUP_MIN): EventLogRow<T>[] {
    const rows: EventLogRow<T>[] = [];
    let start = 0;
    while (start < items.length) {
        const key = items[start].groupKey;
        let end = start + 1;
        if (key) {
            while (end < items.length && items[end].groupKey === key) end++;
        }
        if (key && end - start >= minRun) {
            rows.push({ kind: "group", key, items: items.slice(start, end) });
        } else {
            for (const item of items.slice(start, end)) rows.push({ kind: "single", item });
        }
        start = end;
    }
    return rows;
}

const timeFormatter = new Intl.DateTimeFormat("fr-FR", { timeStyle: "short" });
const dateTimeFormatter = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" });

/** "11:37 → 11:38", with the dates when the run spans several days. Order of the bounds does not matter. */
export function formatEventSpan(first: string, last: string): string {
    const [from, to] = [new Date(first), new Date(last)].sort((a, b) => a.getTime() - b.getTime());
    const sameDay = from.toDateString() === to.toDateString();
    const format = sameDay ? timeFormatter : dateTimeFormatter;
    const start = format.format(from);
    const end = format.format(to);
    return start === end ? start : `${start} → ${end}`;
}
